import { useEffect, useState } from 'react';
import { Card, Button, Spinner, FormField, Badge } from '../components/ui';

const PRIORITIES = [
  { value:'high',   label:'High',   icon:'🔴', color:'#dc2626' },
  { value:'medium', label:'Medium', icon:'🟡', color:'#d97706' },
  { value:'low',    label:'Low',    icon:'🟢', color:'#16a34a' },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

const EMPTY_FORM = { title:'', description:'', due_date:'', priority:'medium', property_id:'' };

export default function Tasks() {
  const [tasks, setTasks]         = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editID, setEditID]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filter, setFilter]       = useState('open');
  const [generating, setGenerating] = useState(false);

  const ac = localStorage.getItem('lc_access_code');
  const h = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('token')}`, ...(ac ? { 'X-Access-Code': ac } : {}) });

  async function load() {
    const [t, p] = await Promise.all([
      fetch(`/api/tasks${filter === 'all' ? '?status=all' : ''}`, { headers: h() }).then(r => r.json()),
      fetch('/api/properties', { headers: h() }).then(r => r.json()),
    ]);
    setTasks(Array.isArray(t) ? t : []);
    setProperties(Array.isArray(p) ? p : []);
    setLoading(false);
  }

  useEffect(() => { load(); }, [filter]);

  async function generateTasks() {
    setGenerating(true);
    await fetch('/api/tasks/generate', { headers: h() });
    await load();
    setGenerating(false);
  }

  function openNew() {
    setForm(EMPTY_FORM);
    setEditID(null);
    setShowForm(true);
  }

  function openEdit(t) {
    setForm({
      title: t.title, description: t.description || '',
      due_date: t.due_date || '', priority: t.priority,
      property_id: t.property_id || '',
    });
    setEditID(t.id);
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const body = {
        ...form,
        property_id: form.property_id || null,
        description: form.description || null,
        due_date: form.due_date || null,
        status: 'open',
      };
      if (editID) {
        await fetch(`/api/tasks/${editID}`, { method:'PUT', headers: h(), body: JSON.stringify(body) });
      } else {
        await fetch('/api/tasks', { method:'POST', headers: h(), body: JSON.stringify(body) });
      }
      await load();
      setShowForm(false);
    } finally { setSaving(false); }
  }

  async function markDone(id) {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    await fetch(`/api/tasks/${id}`, { method:'PUT', headers: h(), body: JSON.stringify({ ...task, status:'done' }) });
    setTasks(ts => ts.filter(t => t.id !== id));
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this task?')) return;
    await fetch(`/api/tasks/${id}`, { method:'DELETE', headers: h() });
    setTasks(ts => ts.filter(t => t.id !== id));
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const high   = tasks.filter(t => t.priority === 'high');
  const medium = tasks.filter(t => t.priority === 'medium');
  const low    = tasks.filter(t => t.priority === 'low');

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:32, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Tasks</h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>Track compliance actions and property management tasks</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Button variant="ghost" loading={generating} onClick={generateTasks}>⚡ Auto-generate</Button>
          <Button onClick={openNew}>+ Add task</Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        {['open','all'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding:'6px 16px', borderRadius:20, border:'1.5px solid var(--border)',
            background: filter === f ? 'var(--ink)' : 'transparent',
            color: filter === f ? '#fff' : 'var(--ink)', fontSize:13, cursor:'pointer', fontWeight:500,
          }}>
            {f === 'open' ? `Open (${tasks.length})` : 'All tasks'}
          </button>
        ))}
      </div>

      {tasks.length === 0 ? (
        <Card style={{ textAlign:'center', padding:48 }}>
          <p style={{ fontSize:32, marginBottom:12 }}>✅</p>
          <p style={{ fontWeight:600, marginBottom:8 }}>No open tasks</p>
          <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:20 }}>
            Tasks are auto-generated from compliance gaps or you can add them manually.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
            <Button variant="ghost" onClick={generateTasks}>⚡ Auto-generate from certs</Button>
            <Button onClick={openNew}>+ Add manually</Button>
          </div>
        </Card>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
          {[
            { label:'High Priority', tasks:high, color:'#dc2626' },
            { label:'Medium Priority', tasks:medium, color:'#d97706' },
            { label:'Low Priority', tasks:low, color:'#16a34a' },
          ].filter(g => g.tasks.length > 0).map(group => (
            <div key={group.label}>
              <p style={{ fontSize:13, fontWeight:600, color:group.color, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>
                {group.label} · {group.tasks.length}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {group.tasks.map(t => (
                  <Card key={t.id} style={{ padding:'14px 18px' }}>
                    <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                      <button onClick={() => markDone(t.id)} style={{
                        width:22, height:22, borderRadius:'50%', border:'2px solid var(--border)',
                        background:'transparent', cursor:'pointer', flexShrink:0, marginTop:2,
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:12,
                      }} title="Mark done">
                        ○
                      </button>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                          <p style={{ fontSize:15, fontWeight:500 }}>{t.title}</p>
                          <div style={{ display:'flex', gap:6 }}>
                            {t.source === 'auto' && <span style={{ fontSize:11, color:'var(--ink-3)', background:'var(--border)', padding:'2px 8px', borderRadius:12 }}>Auto</span>}
                            <Button size="sm" variant="ghost" onClick={() => openEdit(t)}>Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} style={{ color:'var(--danger)' }}>Delete</Button>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:16, marginTop:4, flexWrap:'wrap' }}>
                          {t.due_date && (
                            <span style={{ fontSize:12, color: t.days_until_due !== null && t.days_until_due <= 7 ? '#dc2626' : 'var(--ink-3)' }}>
                              📅 Due {formatDate(t.due_date)}{t.days_until_due !== null ? ` (${t.days_until_due}d)` : ''}
                            </span>
                          )}
                          {t.property_address && <span style={{ fontSize:12, color:'var(--ink-3)' }}>🏘 {t.property_address}</span>}
                        </div>
                        {t.description && <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:6, lineHeight:1.5 }}>{t.description}</p>}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:480, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:20 }}>{editID ? 'Edit task' : 'Add task'}</h3>

            <FormField label="Title *">
              <input value={form.title} onChange={set('title')} placeholder="e.g. Renew gas safety certificate" />
            </FormField>

            <FormField label="Property">
              <select value={form.property_id} onChange={set('property_id')}>
                <option value="">All properties / general</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
              </select>
            </FormField>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:12 }}>
              <FormField label="Due date">
                <input type="date" value={form.due_date} onChange={set('due_date')} />
              </FormField>
              <FormField label="Priority">
                <select value={form.priority} onChange={set('priority')}>
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                </select>
              </FormField>
            </div>

            <FormField label="Notes">
              <textarea value={form.description} onChange={set('description')} rows={3} placeholder="Additional details…" />
            </FormField>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button loading={saving} onClick={handleSave} disabled={!form.title}>
                {editID ? 'Save changes' : 'Add task'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
